<template>
  <v-container class="px-12">
    <h2 class="mb-4">Reasignación de Registros</h2>
    <v-row class="mx-0 my-3">
      <!-- Donador -->
      <v-col cols="12" md="6" class="pa-0 pr-md-2 mb-4 mb-md-0">
        <v-card class="pa-6">
          <v-autocomplete
            v-model="userDonor"
            :items="donorUsers"
            :loading="loadingDonorUsers"
            v-model:search="searchDonor"
            :item-title="userFullName"
            item-value="id"
            label="Usuario donador"
            hide-details
            clearable
            @update:modelValue="onSelectDonor"
            @update:search="fetchDonorUsersDebounced"
            variant="solo-filled"
          />
          <v-divider class="my-4" />

          <!-- Checkbox para seleccionar todo -->
          <div class="d-flex align-center mb-3">
            <v-checkbox
              v-model="selectAll"
              :indeterminate="isIndeterminateAll"
              hide-details
              label="Seleccionar todo"
              class="mr-2"
              density="compact"
              @change="toggleSelectAll"
            />
          </div>

          <div v-if="loadingRecords" class="text-center py-5">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <template v-else>
            <div v-for="type in recordTypes" :key="type?.key" class="mb-3">
                <!-- Collapse Header -->
                <v-row class="d-flex align-center ma-0">
                    
                    <v-checkbox
                    v-model="groupSelection[type.key]"
                    :indeterminate="isIndeterminateGroup(type.key)"
                    hide-details
                    class="mr-2"
                    density="compact"
                    @change="toggleGroupSelect(type.key)"
                    />
                    <div class="font-weight-bold mb-1">{{ type.label + ' (' + records[type.key].length + ')'}}</div>
                    <v-spacer/>
                    <v-btn
                    icon
                    size="small"
                    variant="text"
                    @click="toggleCollapse(type.key)"
                    class="mr-2"
                    >
                        <v-icon>
                            {{ collapsed[type.key] ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                        </v-icon>
                    </v-btn>
                </v-row>

                <v-expand-transition>
                    <div v-show="!collapsed[type.key]" class="pl-7">
                    <v-checkbox
                        v-for="record in records[type.key] || []"
                        :key="record.id"
                        v-model="selected[type.key]"
                        :label="typeRecordLabel(type.key, record)"
                        :value="record.id"
                        density="compact"
                        hide-details
                    />
                    <div v-if="!(records[type.key]?.length)" class="grey--text caption ml-3">Sin registros</div>
                    </div>
                </v-expand-transition>
                </div>

          </template>
        </v-card>
      </v-col>

      <!-- Receptor / Lado derecho -->
      <v-col cols="12" md="6" class="pa-0 pl-md-2 ">
        <v-card class="pa-6">
          <v-autocomplete
            variant="solo-filled"
            v-model="userReceiver"
            :items="receiverUsers"
            :loading="loadingReceiverUsers"
            v-model:search="searchReceiver"
            :item-title="userFullName"
            item-value="id"
            label="Usuario receptor"
            hide-details
            clearable
            :disabled="!userDonor"
            @update:search="fetchReceiverUsersDebounced"
          />
          <v-divider class="my-4" />

          <!-- Mostrar registros seleccionados agrupados por tipo -->
          <div style="max-height: calc(100vh - 450px) !important;     overflow-y: scroll;">
            <div v-for="type in recordTypes" :key="type?.key">
                <div class="font-weight-bold mb-2">{{ type.label }}</div>
                <v-chip
                v-for="id in selected[type.key]"
                :key="id"
                class="ma-1"
                closable
                @click:close="removeSelected(type.key, id)"
                >
                {{ typeRecordLabel(type.key, getRecordById(type.key, id)) }}
                </v-chip>
            </div>
          </div>
          
          <v-btn
            color="primary"
            block
            large
            class="mt-6"
            :loading="transferring"
            :disabled="!userReceiver || totalSelected === 0"
            @click="transferRecords"
          >
            Transferir {{ totalSelected }} registro<span v-if="totalSelected !== 1">s</span>
          </v-btn>
          <v-btn
            color="warning"
            variant="outlined"
            block
            class="mt-3"
            :disabled="!userReceiver || totalRecords === 0"
            @click="showTransferAllDialog = true"
          >
            Reasignar todos los {{ totalRecords }} registros
          </v-btn>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showTransferAllDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">Confirmar reasignación total</v-card-title>
        <v-card-text>
          Se reasignarán todos los {{ totalRecords }} registros de este usuario, incluidos clientes,
          prospectos, cotizaciones, oportunidades, actividades y ventas. Esta acción no se puede deshacer desde esta pantalla.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="transferring" @click="showTransferAllDialog = false">Cancelar</v-btn>
          <v-btn color="warning" :loading="transferring" @click="transferAllRecords">Reasignar todo</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>


<script setup lang="ts">
import { ref, computed, reactive, watch, onMounted } from 'vue';
import { useUsersStore } from '@/store/users';
import debounce from 'lodash/debounce';

const usersStore = useUsersStore();

const userDonor = ref<number | null>(null);
const userReceiver = ref<number | null>(null);

const donorUsers = ref<any[]>([]);
const receiverUsers = ref<any[]>([]);
const searchDonor = ref('');
const searchReceiver = ref('');
const loadingDonorUsers = ref(false);
const loadingReceiverUsers = ref(false);

const loadingRecords = ref(false);
const transferring = ref(false);
const showTransferAllDialog = ref(false);

const recordTypes = [
  { key: 'clients', label: 'Clientes' },
  { key: 'quotations', label: 'Cotizaciones' },
  { key: 'opportunities', label: 'Oportunidades' },
  { key: 'activities', label: 'Actividades' },
  { key: 'leads', label: 'Prospectos' },
  { key: 'sales', label: 'Ventas' },
];

const records = reactive({
  clients: [],
  quotations: [],
  opportunities: [],
  activities: [],
  leads: [],
  sales: [],
}) as any;

// Seleccionados por grupo
const selected = reactive({
  clients: [],
  quotations: [],
  opportunities: [],
  activities: [],
  leads: [],
  sales: [],
}) as any;

// Estados de colapso
const collapsed = reactive({
  clients: true,
  quotations: true,
  opportunities: true,
  activities: true,
  leads: true,
  sales: true,
});

// Selección de grupo (checkbox header de cada grupo)
const groupSelection = reactive({
  clients: false,
  quotations: false,
  opportunities: false,
  activities: false,
  leads: false,
  sales: false,
});

// Checkbox global
const selectAll = ref(false);

const mergeSelectedUser = (items: any[], selectedId: number | null, currentItems: any[]) => {
  if (!selectedId) return items;
  const selected = currentItems.find((user: any) => Number(user.id) === Number(selectedId));
  if (!selected || items.some((user: any) => Number(user.id) === Number(selectedId))) {
    return items;
  }
  return [selected, ...items];
};

const fetchUsers = async (search = '') => {
  const params = search ? { 'filter[name]': search } : {};
  const { data } = await usersStore.searchUsers(params);
  return data;
};

const fetchDonorUsers = async (search = '') => {
  loadingDonorUsers.value = true;
  try {
    const data = await fetchUsers(search);
    donorUsers.value = mergeSelectedUser(data, userDonor.value, donorUsers.value);
  } finally {
    loadingDonorUsers.value = false;
  }
};

const fetchReceiverUsers = async (search = '') => {
  loadingReceiverUsers.value = true;
  try {
    const data = await fetchUsers(search);
    receiverUsers.value = mergeSelectedUser(data, userReceiver.value, receiverUsers.value);
  } finally {
    loadingReceiverUsers.value = false;
  }
};

const fetchDonorUsersDebounced = debounce((q) => fetchDonorUsers(q), 300);
const fetchReceiverUsersDebounced = debounce((q) => fetchReceiverUsers(q), 300);

const userFullName = (user: any) =>
  [user?.name, user?.last_name ?? user?.last].filter(Boolean).join(' ') || user?.name || '';

const onSelectDonor = async (id: number) => {
  Object.keys(records).forEach(key => {
    records[key] = [];
    selected[key] = [];
    collapsed[key] = true;
    groupSelection[key] = false;
  });
  if (!id) return;
  loadingRecords.value = true;
  try {
    const data = await usersStore.fetchAssignedRecords(id) as any;
    recordTypes.forEach(type => {
      records[type.key] = data[type.key]?.items || [];
      selected[type.key] = [];
      collapsed[type.key] = true;
      groupSelection[type.key] = false;
    });
  } finally {
    loadingRecords.value = false;
  }
};

function subjectName(subject: any): string {
  const attributes = subject?.attributes ?? subject;

  return attributes?.commercial_name
    || attributes?.legal_name
    || [attributes?.name, attributes?.last].filter(Boolean).join(' ')
    || '';
}

function withSubject(label: string, subject: any): string {
  const name = subjectName(subject);
  return name ? `${label} - ${name}` : label;
}

// Etiqueta amigable por tipo
function typeRecordLabel(type:any, record:any) {
  if (!record) return '';
  if (type === 'clients') {
    return record.attributes?.commercial_name || record.attributes?.legal_name || `ID ${record.id}`;
  }
  if (type === 'quotations') {
    return withSubject(`Cotización #${record.id}`, record.subject);
  }
  if (type === 'opportunities') {
    return withSubject(record.opportunity_name || `Oportunidad #${record.id}`, record.subject);
  }
  if (type === 'activities') {
    return withSubject(record.title || record.description || `Actividad #${record.id}`, record.subject);
  }
  if (type === 'leads') {
    return [record.name, record.last].filter(Boolean).join(' ') || `ID ${record.id}`;
  }
  if (type === 'sales') {
    return withSubject(`Venta #${record.id}`, record.client || record.account || record.quotation?.subject);
  }
  return record.id;
}

// --- Group & Global select logic ---

// Actualiza la selección global según lo seleccionado en grupos
watch(selected, () => {
  const allSelected = recordTypes.every(
    t => selected[t.key].length === records[t.key].length && records[t.key].length > 0
  );
  const noneSelected = recordTypes.every(
    t => selected[t.key].length === 0
  );
  selectAll.value = allSelected;
}, { deep: true });

// Para cada grupo, mantiene actualizada la selección del checkbox header
watch(selected, () => {
  recordTypes.forEach(type => {
    groupSelection[type.key] =
      selected[type.key].length === records[type.key].length && records[type.key].length > 0;
  });
}, { deep: true });

// Indeterminado para grupo
const isIndeterminateGroup = (key: string) =>
  selected[key].length > 0 && selected[key].length < records[key].length;

// Indeterminado para global
const isIndeterminateAll = computed(() => {
  const total = recordTypes.reduce((sum, t) => sum + records[t.key].length, 0);
  const selectedTotal = recordTypes.reduce((sum, t) => sum + selected[t.key].length, 0);
  return selectedTotal > 0 && selectedTotal < total;
});

// Selecciona/deselecciona todo global
function toggleSelectAll() {
  if (selectAll.value) {
    // Seleccionar todos
    recordTypes.forEach(type => {
      selected[type.key] = records[type.key].map((r: any) => r.id);
    });
  } else {
    // Deseleccionar todos
    recordTypes.forEach(type => {
      selected[type.key] = [];
    });
  }
}

// Selecciona/deselecciona todos en grupo
function toggleGroupSelect(key: string) {
  if (groupSelection[key]) {
    selected[key] = records[key].map((r: any) => r.id);
  } else {
    selected[key] = [];
  }
}

// Expand/collapse grupos
function toggleCollapse(key: string) {
  collapsed[key] = !collapsed[key];
}

// Quitar seleccionado desde el lado derecho
function removeSelected(key: string, id: any) {
  selected[key] = selected[key].filter((item: any) => item !== id);
}

// Obtener registro por id
function getRecordById(type: string, id: any) {
  return records[type].find((r: any) => r.id === id);
}

const totalSelected = computed(() =>
  recordTypes.reduce((sum, t) => sum + (selected[t.key]?.length || 0), 0)
);

const totalRecords = computed(() =>
  recordTypes.reduce((sum, type) => sum + (records[type.key]?.length || 0), 0)
);

const transferRecords = async () => {
  if (!userDonor.value || !userReceiver.value || totalSelected.value === 0) return;
  transferring.value = true;
  try {
    // SOLO MANDAR LOS QUE TIENEN ALGO SELECCIONADO
    const selectedToSend = {};
    Object.keys(selected).forEach(key => {
      if (selected[key] && selected[key].length > 0) {
        selectedToSend[key] = selected[key];
      }
    });
    await usersStore.reassignRecords({
      from_user_id: userDonor.value,
      to_user_id: userReceiver.value,
      selected: selectedToSend,
    });
    onSelectDonor(userDonor.value);
    userReceiver.value = null;
    Object.keys(selected).forEach(k => selected[k] = []);
  } catch (e) {}
  finally { transferring.value = false; }
};

const transferAllRecords = async () => {
  if (!userDonor.value || !userReceiver.value || totalRecords.value === 0) return;
  transferring.value = true;
  try {
    await usersStore.reassignRecords({
      from_user_id: userDonor.value,
      to_user_id: userReceiver.value,
      transfer_all: true,
    });
    showTransferAllDialog.value = false;
    await onSelectDonor(userDonor.value);
    userReceiver.value = null;
    Object.keys(selected).forEach(k => selected[k] = []);
  } catch (e) {
    // The store displays the API error to the user.
  } finally {
    transferring.value = false;
  }
};


onMounted(() => {
  fetchDonorUsers();
  fetchReceiverUsers();
});
</script>
